import { Button, makeStyles, Tooltip } from '@material-ui/core';
import ColorizeIcon from '@material-ui/icons/Colorize';
import classnames from 'classnames';
import React from 'react';

import { DspStatus } from 'src/drugScreening/types';
import { useSWRPatientHighlights } from 'src/patientHeader/components/badges/queries/useSWRPatientHighlights';
import { useButtonStyles } from 'src/patientHeader/components/badges/styles/useButtonStyles';
import { useTooltipStyles } from 'src/patientHeader/components/badges/styles/useTooltipStyles';

const ScreeningStatusLabelMap = {
  [DspStatus.Complete]: 'complete',
  [DspStatus.OnTrack]: 'on track',
  [DspStatus.AtRisk]: 'at risk',
  [DspStatus.RegulatoryPause]: 'reg. pause',
  [DspStatus.BuprenorphineNegativeProtocol]: 'bup neg',
};

const ScreeningStatusColorMap = {
  [DspStatus.Complete]: 'neutral',
  [DspStatus.OnTrack]: 'success',
  [DspStatus.AtRisk]: 'warning',
  [DspStatus.RegulatoryPause]: 'error',
  [DspStatus.BuprenorphineNegativeProtocol]: 'warning',
};

export const DrugScreeningStatusLabel = ({ screeningStatus, defaultLabel = '+' }) => {
  const styles = useButtonStyles();

  const shortText = ScreeningStatusLabelMap[screeningStatus] ?? defaultLabel;
  const statusClass = ScreeningStatusColorMap[screeningStatus] ?? styles.neutral;

  return (
    <span className={styles[statusClass] ?? styles.neutral} data-dsp-status={screeningStatus}>
      {shortText}
    </span>
  );
};

export const DrugScreeningPeriodStatus: React.FC<{ patientId: string }> = ({ patientId }) => {
  const { data } = useSWRPatientHighlights(patientId);
  const buttonStyles = useButtonStyles();
  const tooltipStyles = useTooltipStyles();
  const styles = useStyles();

  if (!data?.patientHighlights) {
    return null; // loading
  }

  if (!data.patientHighlights.hasOudDiagnosis) {
    return null; // DSP is only relevant when patient has OUD diagnosis
  }

  const label = data.patientHighlights.dspStatus?.label ? (
    `Drug screening status: ${data.patientHighlights.dspStatus?.label}`
  ) : (
    <em>Click to add drug screening status</em>
  );
  const value = data.patientHighlights.dspStatus?.value;

  const shortText = value ? ScreeningStatusLabelMap[value] : '+';

  const statusClass = value ? ScreeningStatusColorMap[value] : 'neutral';

  const buttonClassNames = [buttonStyles.button, buttonStyles[statusClass]];

  return (
    <Tooltip
      title={label}
      placement="right"
      enterDelay={300}
      enterNextDelay={300}
      leaveTouchDelay={300}
      classes={{
        tooltip: classnames(tooltipStyles.tooltip),
      }}
    >
      <a href={`/patients/${patientId}/overview#drugScreeningPlan`}>
        <Button
          classes={{ startIcon: styles.startIcon }}
          className={classnames(buttonClassNames)}
          fullWidth
          size="small"
          startIcon={<ColorizeIcon data-testid="dsp-status-icon" />}
          variant="outlined"
        >
          {shortText}
        </Button>
      </a>
    </Tooltip>
  );
};

const useStyles = makeStyles({
  startIcon: {
    marginRight: 2,
  },
});
